import React, { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'

import { OpportunityInterestPresenter } from './opportunity-interest-presenter'
import { OpportunityInterestFormProps } from '../../lib/types/propsType_v1'
import { opportunityServices } from '../../services'
import { showBanner } from '../../global-state/banner-slice'
import { isValidDocType, isValidFileSize } from '../../lib/utils/helpers'
import './opportunity-interest.scss'

const OpportunityInterestContainer = () => {
  const dispatch = useDispatch()
  const [apiResponse, setApiResponse] = useState({ type: '', message: '' })
  const [fileError, setFileError] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const reRef = useRef<ReCAPTCHA>(null)

  const initialValues: OpportunityInterestFormProps = {
    name: '',
    email: '',
    phone: '',
    description: '',
    pitchDeck: null,
  }

  const handleFileChange = async (
    file: File | undefined,
    setFieldValue: (field: string, value: string | null) => void,
  ) => {
    setFileError('')

    if (!file) {
      setSelectedFile(null)
      setFieldValue('pitchDeck', null)
      return
    }

    const isValidType = isValidDocType(file)
    const isValidSize = isValidFileSize(file, 'doc')

    if (!isValidType) {
      setFileError('Invalid file type. Please upload PDF, PPT, or PPTX files.')
      setSelectedFile(null)
      setFieldValue('pitchDeck', null)
      return
    }

    if (!isValidSize) {
      setFileError('File size should be less than 20MB')
      setSelectedFile(null)
      setFieldValue('pitchDeck', null)
      return
    }

    try {
      const response = await opportunityServices.postUploadFiles(file, {})
      if (response && response.status === 200) {
        setSelectedFile(file)
        setFieldValue('pitchDeck', response.data.Location)
      } else {
        setFileError('Failed to upload file. Please try again.')
        setSelectedFile(null)
        setFieldValue('pitchDeck', null)
      }
    } catch (error) {
      setFileError('Failed to upload file. Please try again.')
      setSelectedFile(null)
      setFieldValue('pitchDeck', null)
    }
  }

  const handleSubmit = async (values: OpportunityInterestFormProps) => {
    const token = await reRef.current?.getValue()
    reRef.current?.reset()

    const formData = {
      ...values,
      pitchDeck: values.pitchDeck || null,
    }

    opportunityServices
      .submitOpportunityInterestForm(formData, {
        'g-recaptcha-response': token ? token : '',
      })
      .then((response) => {
        window.scrollTo(0, 0)
        setApiResponse({
          type: 'success',
          message: response.data.message,
        })
        dispatch(
          showBanner({
            text: 'Your opportunity interest has been submitted successfully',
            variant: 'success-banner',
          }),
        )
      })
      .catch((err) => {
        const message = err.response?.data
          ? err.response.data.message
          : 'Error recording your submission'
        setApiResponse({
          type: 'error',
          message: message,
        })
        dispatch(
          showBanner({
            text: 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
      })
  }

  return (
    <div className='flex column-center opportunity-interest-main-layout'>
      <h1 className='opportunity-interest-main-layout__heading mb-24'>Submit Your Opportunity</h1>
      {['', 'error'].includes(apiResponse.type) ? (
        <OpportunityInterestPresenter
          initialValues={initialValues}
          handleFormSubmit={handleSubmit}
          reRef={reRef}
          fileError={fileError}
          selectedFile={selectedFile}
          handleFileChange={handleFileChange}
        />
      ) : (
        <div className='success-message-container'>
          <p className='center dark-gray'>
            Thank you for submitting your opportunity. Your request has been received and a
            Credibila team member will get in touch with you to discuss the opportunity further.
          </p>
        </div>
      )}
    </div>
  )
}

export default OpportunityInterestContainer
